import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
	Button,
	Dropdown,
	Form,
	Header,
	Loader,
	Segment,
	Table,
	TableBody,
	TableCell,
	TableHeader,
	TableHeaderCell,
	TableRow
} from 'semantic-ui-react';
import { OperationsContext } from '../OperationsContext';
import Datepicker from '../../Shared/Datepicker';
import PlanRow from './PlanRow';
import RenewalClientStats from './RenewalClientStats';

const styles = {
	header: { fontWeight: 'normal' },
	loaderContainer: {
		height: '80px',
		position: 'relative'
	}
};

const SetupTab = () => {
	// context
	const {
		renewal,
		renewalYears,
		renewalYear,
		tableLoader,
		updateRenewal,
		handleRenewalYearChange
	} = useContext( OperationsContext );

	const [ plans, setPlans ] = useState( [] );
	const [ start, setStart ] = useState( null );
	const [ end, setEnd ] = useState( null );

	useEffect( () => {
		setPlans( renewal?.plans ?? [] );
		setStart( renewal?.start );
		setEnd( renewal?.end );
	}, [ renewal ] );

	const updatePlans = useCallback( ( id, plan ) => {
		setPlans( prev => prev.map( prevPlan => prevPlan.id === id ? plan : prevPlan ) );
	}, [] );

	const mapPlans = useCallback( () => plans?.map( plan => {
		return (
			<PlanRow
				key={ plan.id }
				plan={ plan }
				updatePlans={ updatePlans }
			/>
		);
	}, [] ), [
		plans,
		updatePlans
	] );

	const handleSave = useCallback( () => {
		try {
			updateRenewal( {
				...renewal,
				plans,
				start,
				end
			} );
		} catch ( error ) {
			console.error( error );
		}
	}, [
		end,
		plans,
		renewal,
		start,
		updateRenewal
	] );

	return (
		<div>
			<Header
				as="h2"
				style={ styles.header }
			>
				Renewal Setup
			</Header>

			{/* filter */}
			<Segment
				style={{
					display: 'flex',
					alignItems: 'center'
				}}
			>
				<span style={{ margin: '0 20px' }}>Renewal Year</span>
				<Dropdown
					value={ renewalYear }
					selection
					options={ renewalYears }
					onChange={ handleRenewalYearChange }
				/>
				<span style={{
					color: '#888',
					margin: '0 20px'
				}}>
					Effective January 1st
				</span>
				<Button
					onClick={ handleSave }
					primary
					style={{ marginLeft: 'auto' }}
				>
						Save
				</Button>
			</Segment>

			{/* table */}
			<Table celled>
				<TableHeader>
					<TableRow>
						<TableHeaderCell colSpan={ 8 }>Global Renewal Percentage By Plan & Coverage Level</TableHeaderCell>
					</TableRow>
					<TableRow>
						<TableHeaderCell>Plan</TableHeaderCell>
						<TableHeaderCell
							collapsing
							style={{ minWidth: '100px' }}
						>
							EE (%)
						</TableHeaderCell>
						<TableHeaderCell
							collapsing
							style={{ minWidth: '100px' }}
						>
							ES (%)
						</TableHeaderCell>
						<TableHeaderCell
							collapsing
							style={{ minWidth: '100px' }}
						>
							EC (%)
						</TableHeaderCell>
						<TableHeaderCell
							collapsing
							style={{ minWidth: '100px' }}
						>
							FAM (%)
						</TableHeaderCell>
						<TableHeaderCell>Avg. Increase (%)</TableHeaderCell>
						<TableHeaderCell>Projected Premium</TableHeaderCell>
						<TableHeaderCell>$ Change</TableHeaderCell>
					</TableRow>
				</TableHeader>
				<TableBody>
					{ tableLoader
						? <TableRow>
							<TableCell colSpan={ 8 }>
								<div style={styles.loaderContainer}>
									<Loader active />
								</div>
							</TableCell>
						</TableRow>
						: mapPlans()
					}
				</TableBody>
			</Table>

			{/* range */}
			<Segment>
				<Form>
					<Form.Group style={{ alignItems: 'center', marginBottom: 0 }}>
						<span style={{ margin: ' 0 20px' }}>Reporting Range:</span>
						<Datepicker
							name="start"
							selected={ start ? new Date( start ) : '' }
							onChange={ date => {
								setStart( date );
							} }
							width={ 3 }
						/>
						<span style={{ margin: '0 0.5rem' }}>to</span>
						<Datepicker
							name="end"
							selected={ end ? new Date( end ) : '' }
							onChange={ date => {
								setEnd( date );
							} }
							width={ 3 }
						/>
					</Form.Group>
				</Form>
			</Segment>

			{/* stats */}
			<RenewalClientStats />
		</div>
	);
};

export default SetupTab;

